import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import type { WizardPerfectDayData } from '~/features/onboard/wizard-state';

interface SpecField<T> {
  value: T;
  provenance: string;
  updatedAt: string;
}

export interface PerfectDaySpecResponse {
  identity?: SpecField<string>;
  mustDos?: SpecField<string[]>;
  focus?: SpecField<string>;
  people?: SpecField<{ name: string; kind: 'private' | 'professional'; note?: string }[]>;
}

export const perfectDaySpecKeys = {
  current: () => ['perfect-day-spec', 'current'] as const,
};

/**
 * FRONT-1103: maps a fetched spec back onto the wizard's local
 * `perfectDay` shape, for the wizard's hydrate-on-mount effect.
 *
 * `focus` is deliberately NOT included — it's saved as one joined
 * free-text string (`handleMustDosFocusNext` in `onboard.tsx`) with no way
 * back to the 4 structured booleans/strings (`focusProtectWork` etc.) the
 * step edits, so there's nothing correct to reconstruct here. That's safe:
 * the save handler only ever sends `focus` when it's non-empty, so an
 * unhydrated (default-false) focus step can't blank out an already-saved
 * value the way an unhydrated `people`/`mustDos` step silently could.
 */
export function buildPerfectDayHydration(
  spec: PerfectDaySpecResponse | undefined,
): Partial<WizardPerfectDayData> {
  if (!spec) return {};
  return {
    ...(spec.identity && { title: spec.identity.value }),
    ...(spec.mustDos && { mustDos: spec.mustDos.value }),
    ...(spec.people && { people: spec.people.value }),
  };
}

/**
 * FRONT-1103: the quickstart wizard writes these fields directly as the
 * user steps through (`useSavePerfectDaySpecFields`), but until this hook
 * nothing ever read them back — so cancelling and re-entering the wizard
 * always restarted from blank local state, and re-saving a step (even an
 * empty one, e.g. People with zero re-added contacts) silently overwrote
 * whatever was already saved for that field. See `onboard.tsx`'s hydrate
 * effect for where this feeds back into the wizard's local state.
 *
 * PR-panel finding: `staleTime: Infinity` (matching `useIsOnboarded`'s own
 * one-time-hydration use of the same pattern) means this only ever
 * refetches on an explicit invalidation — `useSavePerfectDaySpecFields`'s
 * `onSuccess` does exactly that. Without it, a background refetch (e.g. on
 * network reconnect) could land mid-session and re-fire the wizard's
 * hydrate effect with data from before the user's own in-progress edits,
 * clobbering what they'd just typed.
 */
export function usePerfectDaySpec() {
  return useQuery({
    queryKey: perfectDaySpecKeys.current(),
    queryFn: () => api.get<PerfectDaySpecResponse>('/api/me/perfect-day-spec'),
    staleTime: Infinity,
  });
}
